import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import HomepageHeader from '../homepage/components/HomepageHeader';
import FooterSection from '../homepage/components/FooterSection';
import { partnerFinderService, partnerCategoryService } from '../../services/partnerCategoryService';
import { useViewport } from '../../hooks/useMobileGestures';

// Fix Leaflet default marker icon issue
delete L?.Icon?.Default?.prototype?._getIconUrl;
L?.Icon?.Default?.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const PartnerFinder = () => {
  const viewport = useViewport();
  const [partners, setPartners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [radiusKm, setRadiusKm] = useState('25');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [sortBy, setSortBy] = useState('name'); // 'name' or 'distance'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [partnersData, categoriesData] = await Promise.all([
        partnerFinderService?.getVisiblePartners(),
        partnerCategoryService?.getAll()
      ]);
      setPartners(partnersData);
      setCategories(categoriesData?.filter(cat => cat?.isVisible));
    } catch (err) {
      setError(err?.message || 'Fehler beim Laden der Partner');
      console.error('Error loading partner finder data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      setError('');
      const results = await partnerFinderService?.searchByPostalCode(
        postalCode,
        selectedCategory || null,
        radiusKm ? parseInt(radiusKm) : null
      );
      setPartners(results);
    } catch (err) {
      setError(err?.message || 'Fehler bei der Suche');
      console.error('Error searching partners:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setPostalCode('');
    setRadiusKm('25');
    setSelectedCategory('');
    setSearchQuery('');
    loadData();
  };

  // Filter partners by search query
  const filteredPartners = partners?.filter(partner => {
    const matchesSearch = !searchQuery || 
      partner?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      partner?.city?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      partner?.postalCode?.includes(searchQuery);
    
    return matchesSearch;
  });

  // Sort partners
  const sortedPartners = [...filteredPartners]?.sort((a, b) => {
    if (sortBy === 'name') {
      return a?.name?.localeCompare(b?.name);
    }
    return 0; // Distance sorting would require geolocation calculation
  });

  // Get category options for select
  const categoryOptions = [
    { value: '', label: 'Alle Kategorien' },
    ...categories?.map(cat => ({
      value: cat?.id,
      label: cat?.name
    }))
  ];

  // Calculate map center (Germany center as default)
  const mapCenter = [51.1657, 10.4515];
  const mapZoom = 6;

  if (loading && partners?.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Partner werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <HomepageHeader />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-3">
            Partner finden
          </h1>
          <p className="text-lg text-muted-foreground">
            Finden Sie THG GridBalance Partner in Ihrer Nähe
          </p>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
              <div className="flex-1">
                <p className="font-medium text-destructive">Fehler</p>
                <p className="text-sm text-destructive/80 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Search Controls */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <Input
              label="Postleitzahl"
              type="text"
              placeholder="z.B. 80331"
              value={postalCode}
              onChange={(e) => setPostalCode(e?.target?.value)}
              leftIcon="MapPin"
            />
            
            <Select
              label="Umkreis"
              options={[
                { value: '5', label: '5 km' },
                { value: '10', label: '10 km' },
                { value: '25', label: '25 km' },
                { value: '50', label: '50 km' },
                { value: '100', label: '100 km' },
                { value: '', label: 'Alle' }
              ]}
              value={radiusKm}
              onChange={setRadiusKm}
            />
            
            <Select
              label="Kategorie"
              options={categoryOptions}
              value={selectedCategory}
              onChange={setSelectedCategory}
            />

            <Input
              label="Suche"
              type="text"
              placeholder="Name oder Stadt"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              leftIcon="Search"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              variant="default"
              iconName="Search"
              onClick={handleSearch}
              disabled={loading}
            >
              Suchen
            </Button>
            <Button
              variant="outline"
              iconName="RotateCcw"
              onClick={handleReset}
              disabled={loading}
            >
              Zurücksetzen
            </Button>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 rounded-lg transition-smooth flex items-center gap-2 ${
                viewMode === 'list' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <Icon name="List" size={18} />
              Liste
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`px-4 py-2 rounded-lg transition-smooth flex items-center gap-2 ${
                viewMode === 'map' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <Icon name="Map" size={18} />
              Karte
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Sortieren:</span>
            <Select
              options={[
                { value: 'name', label: 'Name' },
                { value: 'distance', label: 'Entfernung' }
              ]}
              value={sortBy}
              onChange={setSortBy}
              className="w-40"
            />
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-sm text-muted-foreground">
            {sortedPartners?.length} Partner gefunden
          </p>
        </div>

        {/* Map View */}
        {viewMode === 'map' && (
          <div className="bg-card border border-border rounded-xl overflow-hidden mb-6" style={{ height: '500px' }}>
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {sortedPartners?.map(partner => {
                // Only show partners with valid coordinates
                if (!partner?.latitude || !partner?.longitude) {
                  return null;
                }
                
                return (
                  <Marker key={partner?.id} position={[partner?.latitude, partner?.longitude]}>
                    <Popup>
                      <div className="p-2">
                        <h3 className="font-semibold text-foreground mb-1">{partner?.name}</h3>
                        <p className="text-sm text-muted-foreground mb-1">
                          {partner?.street}<br />
                          {partner?.postalCode} {partner?.city}
                        </p>
                        {partner?.categoryName && (
                          <span className="inline-block px-2 py-1 bg-primary/10 text-primary text-xs rounded-full mb-2">
                            {partner?.categoryName}
                          </span>
                        )}
                        {partner?.distance !== undefined && (
                          <p className="text-xs text-muted-foreground mb-2">
                            <Icon name="Navigation" size={12} className="inline mr-1" />
                            {partner?.distance?.toFixed(1)} km entfernt
                          </p>
                        )}
                        <div className="flex gap-2 mt-2">
                          {partner?.phone && (
                            <a href={`tel:${partner?.phone}`} className="text-primary text-sm hover:underline">
                              Anrufen
                            </a>
                          )}
                          {partner?.website && (
                            <a href={partner?.website} target="_blank" rel="noopener noreferrer" className="text-primary text-sm hover:underline">
                              Website
                            </a>
                          )}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedPartners?.map(partner => (
              <div key={partner?.id} className="bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-md transition-smooth">
                {/* Partner Logo */}
                {partner?.logo && (
                  <div 
                    className="w-full h-24 rounded-lg overflow-hidden flex items-center justify-center mb-4 border border-border"
                    style={{ backgroundColor: partner?.logoBackgroundColor || '#FFFFFF' }}
                  >
                    <img
                      src={partner?.logo}
                      alt={partner?.logoAlt || `Logo von ${partner?.name}`}
                      className="max-w-full max-h-full object-contain p-2"
                    />
                  </div>
                )}

                {/* Partner Info */}
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {partner?.name}
                </h3>
                
                {partner?.categoryName && (
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name={partner?.categoryIcon || 'Building2'} size={16} color="var(--color-primary)" />
                    <span className="text-sm text-primary font-medium">{partner?.categoryName}</span>
                  </div>
                )}

                {partner?.distance !== undefined && (
                  <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
                    <Icon name="Navigation" size={16} />
                    <span>{partner?.distance?.toFixed(1)} km entfernt</span>
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Icon name="MapPin" size={16} className="mt-0.5 flex-shrink-0" />
                    <span>
                      {partner?.street}<br />
                      {partner?.postalCode} {partner?.city}
                    </span>
                  </div>
                  
                  {partner?.phone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Phone" size={16} />
                      <span className="font-medium text-foreground">Telefon:</span>
                      <a href={`tel:${partner?.phone}`} className="hover:text-primary transition-smooth">
                        {partner?.phone}
                      </a>
                    </div>
                  )}
                  
                  {partner?.email && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Mail" size={16} />
                      <a href={`mailto:${partner?.email}`} className="hover:text-primary transition-smooth truncate">
                        {partner?.email}
                      </a>
                    </div>
                  )}
                </div>

                {/* THG Platform Landing Page Button */}
                {partner?.urlSlug && (
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Gift"
                    onClick={() => window.open(`https://thg.gridbalance.de/${partner?.urlSlug}`, '_blank')}
                    className="w-full mb-3"
                  >
                    Jetzt Prämie beantragen
                  </Button>
                )}
                {/* Action Buttons */}
                <div className="flex gap-2">
                  {partner?.phone && (
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Phone"
                      onClick={() => window.location.href = `tel:${partner?.phone}`}
                      className="flex-1"
                    >
                      Anrufen
                    </Button>
                  )}
                  {partner?.website && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="ExternalLink"
                      onClick={() => window.open(partner?.website, '_blank')}
                      className="flex-1"
                    >
                      Website
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No Results */}
        {sortedPartners?.length === 0 && !loading && (
          <div className="text-center py-12">
            <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">Keine Partner gefunden</h3>
            <p className="text-muted-foreground mb-4">
              Versuchen Sie es mit einer anderen Postleitzahl oder Kategorie
            </p>
            <Button variant="outline" onClick={handleReset} iconName="RotateCcw">
              Filter zurücksetzen
            </Button>
          </div>
        )}
      </div>

      <FooterSection />
    </div>
  );
};

export default PartnerFinder;